import { type FormEvent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { createService, listServices, updateService } from '../../api/services'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { inputClass, selectClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import { formatCurrency } from '@/lib/currency'
import { thClass, tdClass, tbodyHoverClass } from '@/lib/tableStyles'
import type { AppointmentType } from '../../types/appointments'
import { TYPE_LABELS } from '../../types/appointments'
import type { ServiceRead } from '../../types/service'

const APPOINTMENT_TYPES = Object.keys(TYPE_LABELS) as AppointmentType[]

const EMPTY_FORM = {
  name: '',
  appointmentType: '' as AppointmentType | '',
  price: '0.00',
  taxInclusive: false,
}

export default function ServiceCatalog() {
  const { id: practiceIdStr } = useParams<{ id: string }>()
  const practiceId = Number(practiceIdStr)
  const toast = useToast()

  const { data, loading, error, refetch } = useFetch(
    () => listServices({ practice_id: practiceId, include_inactive: true }),
    [practiceId],
  )
  const services: ServiceRead[] = data ?? []

  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const claimedTypes = new Set(
    services.filter(s => s.is_active && s.appointment_type && s.id !== editingId).map(s => s.appointment_type),
  )

  const resetForm = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError(null)
  }

  const startEdit = (s: ServiceRead) => {
    setEditingId(s.id)
    setForm({
      name: s.name,
      appointmentType: s.appointment_type ?? '',
      price: s.default_price,
      taxInclusive: s.tax_inclusive_default,
    })
    setFormError(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (saving || !form.name.trim()) return
    setSaving(true)
    setFormError(null)
    try {
      const payload = {
        name: form.name.trim(),
        default_price: parseFloat(form.price) || 0,
        tax_inclusive_default: form.taxInclusive,
        appointment_type: form.appointmentType || null,
      }
      if (editingId) {
        await updateService(editingId, payload)
        toast.success('Service updated.')
      } else {
        await createService({ practice_id: practiceId, ...payload })
        toast.success('Service added.')
      }
      resetForm()
      refetch()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save service.')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (s: ServiceRead) => {
    try {
      await updateService(s.id, { is_active: !s.is_active })
      toast.success(s.is_active ? 'Service deactivated.' : 'Service reactivated.')
      refetch()
    } catch {
      toast.error('Failed to update service.')
    }
  }

  return (
    <div className="max-w-[900px]">
      <h1 className="m-0 mb-2 text-[1.4rem] font-bold text-text-heading">Service Price List</h1>
      <p className="mt-0 mb-6 text-[0.875rem] text-text-3">
        Set default flat rates for appointment types and custom services. Staff can pull these prices
        straight onto an invoice line item instead of typing a description and price by hand.
      </p>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      {/* Add / edit form */}
      <Card className="mb-5">
        <SectionTitle>{editingId ? 'Edit Service' : 'Add Service'}</SectionTitle>
        {formError && <ErrorBox className="mb-3">{formError}</ErrorBox>}
        <form onSubmit={(e) => void handleSubmit(e)} className="flex gap-3 flex-wrap items-end">
          <div className="flex-[2] min-w-[200px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Consultation, Nail trim…"
              className={`${inputClass()} box-border`}
              maxLength={200}
              required
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Appointment type</label>
            <select
              value={form.appointmentType}
              onChange={(e) => setForm(f => ({ ...f, appointmentType: e.target.value as AppointmentType | '' }))}
              className={`${selectClass()} box-border`}
            >
              <option value="">Custom (not linked)</option>
              {APPOINTMENT_TYPES.map(t => (
                <option key={t} value={t} disabled={claimedTypes.has(t)}>
                  {TYPE_LABELS[t]}{claimedTypes.has(t) ? ' (already set)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[130px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Price (R)</label>
            <input
              type="number"
              value={form.price}
              onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
              className={`${inputClass()} box-border text-right`}
              min={0} step={0.01}
            />
          </div>
          <div className="flex items-center gap-2 pb-[0.55rem]">
            <input
              id="svc-tax-incl"
              type="checkbox"
              checked={form.taxInclusive}
              onChange={(e) => setForm(f => ({ ...f, taxInclusive: e.target.checked }))}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="svc-tax-incl" className="text-[0.8rem] font-semibold text-text-heading cursor-pointer whitespace-nowrap">
              Tax inclusive
            </label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Service'}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* Service list */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Type</th>
                <th className={`${thClass} text-right`}>Price</th>
                <th className={thClass}>Tax incl.</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className="h-3.5 bg-[var(--skeleton-from)] rounded w-[70%]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={6} className={`${tdClass} text-center py-10 text-text-3`}>
                    No services yet — add one above.
                  </td>
                </tr>
              ) : (
                services.map((s) => (
                  <tr key={s.id}>
                    <td className={tdClass}>{s.name}</td>
                    <td className={`${tdClass} text-text-2`}>
                      {s.appointment_type ? TYPE_LABELS[s.appointment_type] : <span className="text-text-4">Custom</span>}
                    </td>
                    <td className={`${tdClass} text-right font-semibold`}>{formatCurrency(s.default_price)}</td>
                    <td className={`${tdClass} text-text-2`}>{s.tax_inclusive_default ? 'Yes' : 'No'}</td>
                    <td className={tdClass}>
                      <span
                        className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
                        style={s.is_active
                          ? { background: 'var(--success-bg)', color: 'var(--success-text)' }
                          : { background: 'var(--surface-alt)', color: 'var(--text-3)' }}
                      >
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <button
                        className="bg-transparent border-none cursor-pointer text-accent text-[0.82rem] font-medium py-[0.2rem] px-[0.4rem]"
                        onClick={() => startEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-transparent border-none cursor-pointer text-text-3 text-[0.82rem] font-medium py-[0.2rem] px-[0.4rem]"
                        onClick={() => void handleToggleActive(s)}
                      >
                        {s.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
